import { Transaction, Connection } from '@solana/web3.js';
import { Buffer } from '../utils/bufferPolyfill';

export interface SignedTransactionData {
  signedTransaction: string;
  signature: string;
  slot: number;
}

export interface TransactionResult {
  success: boolean;
  signature?: string;
  error?: string;
  slot?: number;
}

export class SignedTransactionService {
  private static connection = new Connection('https://api.devnet.solana.com');

  /**
   * Signs and sends a transaction to the Solana network
   */
  static async signAndSendTransaction(
    transactionBase64: string,
    wallet: any
  ): Promise<TransactionResult> {
    try {
      console.log('Starting transaction signing and sending...');
      
      // Deserialize the transaction
      const transaction = Transaction.from(Buffer.from(transactionBase64, 'base64'));
      console.log('Transaction deserialized successfully');

      // Check if transaction already has signatures (partially signed)
      const hasExistingSignatures = transaction.signatures.some(sig => sig.signature !== null);
      console.log('Transaction has existing signatures:', hasExistingSignatures);

      let blockhash: string;
      let lastValidBlockHeight: number;

      if (hasExistingSignatures) {
        // Transaction is already partially signed (e.g., token creation with mint keypair)
        // Do NOT update blockhash as it would invalidate existing signatures
        console.log('Using existing blockhash from partially signed transaction');
        blockhash = transaction.recentBlockhash!;
        
        // Get the block height for the existing blockhash
        const blockheightInfo = await this.connection.getLatestBlockhash('finalized');
        lastValidBlockHeight = blockheightInfo.lastValidBlockHeight;
        
        // Just set fee payer if not set
        if (!transaction.feePayer) {
          transaction.feePayer = wallet.publicKey;
        }
      } else {
        // Fresh transaction, get new blockhash
        const blockhashInfo = await this.connection.getLatestBlockhash('confirmed');
        blockhash = blockhashInfo.blockhash;
        lastValidBlockHeight = blockhashInfo.lastValidBlockHeight;
        console.log('Fresh blockhash obtained:', blockhash);
        
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = wallet.publicKey;
      }

      // Sign the transaction with the wallet
      console.log('Signing transaction with wallet...');
      const signedTransaction = await wallet.signTransaction(transaction);
      console.log('Transaction signed successfully');

      // Send the signed transaction to the network with proper options
      console.log('Sending transaction to network...');
      const signature = await this.connection.sendRawTransaction(
        signedTransaction.serialize(),
        {
          skipPreflight: true,
          preflightCommitment: 'confirmed',
        }
      );
      console.log('Transaction sent, signature:', signature);

      // Wait for confirmation with timeout
      console.log('Waiting for confirmation...');
      const confirmation = await this.connection.confirmTransaction(
        {
          signature,
          blockhash,
          lastValidBlockHeight,
        },
        'confirmed'
      );
      console.log('confirmation', confirmation);
      if (confirmation.value.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
      }
      const slot = confirmation.context.slot;
      console.log('Transaction confirmed successfully');
      return {
        success: true,
        signature,
        slot,
      };
    } catch (error) {
      console.error('Transaction failed:', error);
      
      // Enhanced error handling for SendTransactionError
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check if it's a SendTransactionError and try to get more details
        if (error.name === 'SendTransactionError' && 'getLogs' in error) {
          try {
            const logs = (error as any).getLogs();
            console.error('Transaction logs:', logs);
            errorMessage += ` | Logs: ${JSON.stringify(logs)}`;
          } catch (logError) {
            console.error('Failed to get transaction logs:', logError);
          }
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Rebuilds a transaction with fresh blockhash and fee payer
   */
  static async rebuildTransactionWithFreshBlockhash(
    transactionBase64: string,
    wallet: any
  ): Promise<Transaction> {
    const transaction = Transaction.from(Buffer.from(transactionBase64, 'base64'));
    
    // Get fresh blockhash
    const { blockhash } = await this.connection.getLatestBlockhash('confirmed');
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = wallet.publicKey;
    
    return transaction;
  }

  /**
   * Signs and sends a transaction with retry logic
   */
  static async signAndSendTransactionWithRetry(
    transactionBase64: string,
    wallet: any,
    maxRetries: number = 3
  ): Promise<TransactionResult> {
    let lastError: string | undefined;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // For retries, rebuild the transaction with fresh blockhash
        if (attempt > 1) {
          const freshTransaction = await this.rebuildTransactionWithFreshBlockhash(transactionBase64, wallet);
          const freshTransactionBase64 = freshTransaction.serialize({ requireAllSignatures: false }).toString('base64');
          const result = await this.signAndSendTransaction(freshTransactionBase64, wallet);
          if (result.success) {
            return result;
          }
          lastError = result.error;
        } else {
          const result = await this.signAndSendTransaction(transactionBase64, wallet);
          if (result.success) {
            return result;
          }
          lastError = result.error;
        }
      } catch (error) {
        lastError = error instanceof Error ? error.message : 'Unknown error occurred';
      }

      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }

    return {
      success: false,
      error: lastError || 'Transaction failed after all retry attempts',
    };
  }


}

export default SignedTransactionService;

